import { Component, EventEmitter, Input, Output } from '@angular/core';

@Component({
  selector: 'bus-passenger-filled-info, lounge-passenger-filled-info, esim-passenger-filled-info, fasttrack-passenger-filled-info',
  templateUrl: './passenger-filled-info.component.html',
  styleUrls: ['./passenger-filled-info.component.scss'],
})
export class PassengerFilledInfoComponent {

  private staticPassengerCountDetails: any = {
    adult: 6,
    child: 0,
    infant: 0,
  };

  private _passengerList: any[] = [];
  private _passengerCountDetails: any;

  @Input() set passengerList(value: any[]) {
    this._passengerList = Array.isArray(value) ? value : [];
  }
  get passengerList(): any[] {
    if (this._passengerList && this._passengerList.length > 0) {
      return this._passengerList;
    }
    return this.disableStaticFallback ? [] : [];
  }

  @Input() set passengerCountDetails(value: any) {
    this._passengerCountDetails = value || this.staticPassengerCountDetails;
  }
  get passengerCountDetails(): any {
    return this._passengerCountDetails || this.staticPassengerCountDetails;
  }

  @Input() selectedSeatData?: any;
  @Input() showTitle: boolean = false;
  @Input() showEdit: boolean = true;
  @Input() passengerValidationState: { [index: number]: boolean } = {};
  @Input() showValidationLabels: boolean = false;
  @Input() showRemainingMessage: boolean = false;
  @Input() disableStaticFallback: boolean = false;
  @Input() cancelledSeats: any[] = [];
  @Input() referenceDate: string = '';

  @Output() editPassengerRequested: EventEmitter<{ passenger: any; index: number }> =
    new EventEmitter<{ passenger: any; index: number }>();

  getPassengersByType(type: string): any[] {
    return this.passengerList?.filter((p: any) => p?.type === type) || [];
  }

  getMaxPassengersByType(type: string): number {
    switch (type) {
      case 'adult':
        return this.passengerCountDetails?.adult || 0;
      case 'child':
        return this.passengerCountDetails?.child || 0;
      case 'infant':
        return this.passengerCountDetails?.infant || 0;
      default:
        return 0;
    }
  }

  // Get total number of passengers
  getTotalPassengers(): number {
    return this.passengerList?.length || 0;
  }

  getOriginalIndex(passenger: any): number {
    return this.passengerList?.indexOf(passenger) ?? -1;
  }

  isSeatCancelled(seatNumber: string): boolean {
    return this.cancelledSeats.includes(seatNumber);
  }

  handleEdit(passenger: any) {
    const index = this.getOriginalIndex(passenger);
    if (index >= 0) {
      this.editPassengerRequested.emit({ passenger, index });
    }
  }

  getAgeDisplay(passenger: any): string {
    if (passenger.Age > 0) return `${passenger.Age} ${passenger.Age === 1 ? 'year' : 'years'}`;
    if (passenger.DateOfBirth) return this.calculateAge(passenger.DateOfBirth);
    return '';
  }

  // Calculate age from date of birth
  calculateAge(dateOfBirth: string): string {
    if (!dateOfBirth) return '';
    const parsedReference = this.referenceDate ? new Date(this.referenceDate) : null;
    const today =
      parsedReference && !Number.isNaN(parsedReference.getTime())
        ? parsedReference
        : new Date();
    const birthDate = new Date(dateOfBirth);
    if (Number.isNaN(birthDate.getTime())) return '';

    let years = today.getFullYear() - birthDate.getFullYear();
    let months = today.getMonth() - birthDate.getMonth();
    let days = today.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(today.getFullYear(), today.getMonth(), 0).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    if (years > 0) return `${years} ${years === 1 ? 'year' : 'years'}`;
    if (months > 0) return `${months} ${months === 1 ? 'month' : 'months'}`;
    return `${days} ${days === 1 ? 'day' : 'days'}`;
  }

  // Get seat number for a passenger with fallback
  getSeatNumber(passenger: any, index: number): string {
    // First try to get from passenger object
    if (passenger?.SeatNumber) {
      return passenger.SeatNumber;
    }
    
    // Try to get from selectedSeatData if available
    if (this.selectedSeatData && Array.isArray(this.selectedSeatData)) {
      // Try index-based matching first (assuming seats are in same order as passengers)
      const seat = this.selectedSeatData[index];
      if (seat?.number && seat?.selected) {
        return seat.number;
      }
      
      // Fallback: find first available selected seat that hasn't been assigned
      const selectedSeats = this.selectedSeatData.filter((seat: any) => seat?.selected);
      if (selectedSeats.length > index && selectedSeats[index]?.number) {
        return selectedSeats[index].number;
      }
    }
    
    // Fallback to empty string if no seat found
    return '';
  }
}


